import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, Lan, Refresh } from "@mui/icons-material";
import SwitchToggle from "./SwitchToggle";
import { switchDate } from "../../../model/switchModel";
const palette = {
  success: ["#217346", "#edf8f0"],
  error: ["#b42318", "#fff1f0"],
  warning: ["#885515", "#fff6e6"],
  info: ["#245a83", "#edf4ff"],
  default: ["#526579", "#eef3f8"],
};
export default function PortsModal({
  open,
  onClose,
  switchGroup,
  resetLoading,
  statusChanging,
  onPortSelect,
  onResetClick,
  onChangePortStatus,
  busy = false,
}) {
  const [portId, setPortId] = useState(null);
  useEffect(() => {
    setPortId(null);
  }, [switchGroup?.id, open]);
  const ports = switchGroup?.ports || [];
  const port = ports.find((p) => String(p.portId) === String(portId)) || ports[0];
  const [color, bgcolor] = palette[port?.respuestaColor] || palette.default;
  const disabled = busy || resetLoading || statusChanging;
  return (
    <Dialog
      open={open && Boolean(switchGroup)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="switch-ports-title"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh", m: { xs: 1, sm: 3 } } }}
    >
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: "#193e54", px: 3, py: 2 }}
      >
        <Lan sx={{ color: "#fff" }} />
        <Box sx={{ flex: 1 }}>
          <Typography
            id="switch-ports-title"
            component="h2"
            sx={{ color: "#fff", fontSize: "1rem", fontWeight: 700, overflowWrap: "anywhere" }}
          >
            {switchGroup?.name?.replace(/_/g, " ")}
          </Typography>
          <Typography sx={{ color: "#dbe8f2", fontSize: ".75rem" }}>
            {switchGroup?.ip} · {ports.length} puertos · {switchGroup?.activePorts} con automático
            habilitado
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Cerrar puertos" sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f7fa" }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 2 }}>
          <Chip size="small" label={`Referencia de hardware: ${switchGroup?.hardware}`} />
          <Typography sx={{ fontSize: ".75rem", color: "#526579", overflowWrap: "anywhere" }}>
            Software: {switchGroup?.version}
          </Typography>
        </Box>
        <Typography
          component="h3"
          sx={{ fontSize: ".85rem", fontWeight: 700, color: "#193e54", mb: 1 }}
        >
          Selecciona un puerto
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(90px,1fr))",
            gap: 1,
            mb: 2.5,
          }}
        >
          {ports.map((p) => {
            const [tone, fill] = palette[p.respuestaColor] || palette.default;
            return (
              <Button
                key={p.id}
                onClick={() => setPortId(p.portId)}
                aria-label={`Ver puerto ${p.port}`}
                aria-pressed={p === port}
                sx={{
                  p: 1.25,
                  border: p === port ? "2px solid #245a83" : "1px solid #dce5ed",
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  color: tone,
                  bgcolor: fill,
                  textTransform: "none",
                  "&:hover": { bgcolor: fill, borderColor: "#245a83" },
                }}
              >
                <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: tone }}>
                  #{p.port}
                </Typography>
                <Typography sx={{ fontSize: ".65rem", color: "#526579" }}>
                  Auto {p.resetstatus ? "habilitado" : "deshabilitado"}
                </Typography>
              </Button>
            );
          })}
        </Box>
        {port ? (
          <Box sx={{ p: 2.5, bgcolor: "#fff", border: "1px solid #dce5ed", borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "space-between",
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Typography
                component="h3"
                sx={{ color: "#193e54", fontSize: "1rem", fontWeight: 700 }}
              >
                Puerto {port.port}
              </Typography>
              <Typography sx={{ fontSize: ".75rem", color: "#526579" }}>
                ID: {port.portId ?? "Sin identificador"}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: ".75rem", color: "#526579", mb: 0.75 }}>
              Resultado del último intento
            </Typography>
            <Box sx={{ p: 1.5, bgcolor, borderRadius: 1.5, mb: 2 }}>
              <Typography
                sx={{ fontSize: ".9rem", fontWeight: 600, color, overflowWrap: "anywhere" }}
              >
                {port.resetresponse}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: ".75rem", color: "#526579", mb: 2 }}>
              Fecha registrada · Bogotá: {switchDate(port.resetdate)}
            </Typography>
            <Box
              sx={{
                borderTop: "1px solid #dce5ed",
                pt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: ".75rem", color: "#526579", mb: 0.5 }}>
                  Reinicio automático
                </Typography>
                <SwitchToggle
                  portId={port.portId ?? ""}
                  currentStatus={port.resetstatus}
                  onChangeStatus={onChangePortStatus}
                  disabled={disabled || port.portId == null}
                  statusChanging={statusChanging}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                disabled={disabled || port.portId == null}
                onClick={() => {
                  onPortSelect(port);
                  onResetClick(port);
                }}
                sx={{ color: "#245a83", borderColor: "#9db8cf" }}
              >
                {resetLoading ? "Reiniciando…" : "Reiniciar este puerto"}
              </Button>
            </Box>
          </Box>
        ) : (
          <Alert severity="info">Este switch no tiene puertos registrados.</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #dce5ed" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ color: "#193e54", borderColor: "#c6d3df" }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
PortsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  switchGroup: PropTypes.object,
  resetLoading: PropTypes.bool,
  statusChanging: PropTypes.bool,
  busy: PropTypes.bool,
  onPortSelect: PropTypes.func.isRequired,
  onResetClick: PropTypes.func.isRequired,
  onChangePortStatus: PropTypes.func.isRequired,
};
